// ── Docks mode (the Dock Builder tool, see index.js) ─────────────────────────────────────────
// Draw a marina's docks over the imagery -- walkways, fingers, piles -- and see them become
// catalog components (float modules with seams, finger frames, pile guides) as you draw.
//
// What's stored is only what the user drew (slip-api /v1/dock-assemblies, see
// services/slip-api/src/docks.js); the components are rebuilt on every change by the shared
// generator (services/ais-api/src/docks/assembly.js) from the parts catalog, so a catalog update
// reaches every saved dock. Every change is one atomic edit with its inverse kept client-side,
// so Ctrl+Z / Ctrl+Shift+Z undo and redo whole gestures.
//
// Tools: Walkway (click points; type a length + Enter for an exact segment; Enter or
// double-click to finish), Fingers (click a start and an end along a walkway edge; + / - or a
// typed count + Enter for how many), Fingers from slips (fills the selected walkway from the
// slips along it), Pile (click; Suggest piles proposes finger-tip and walkway piles), Select
// (click a part; edit its numbers in the panel; Delete removes it; drag a walkway's vertices).

import L from "leaflet";
import { buildAssembly, localFrame, projectOnWalkway, fingersFromSlips, suggestPiles, suggestFittings } from "../../../services/ais-api/src/docks/assembly.js";
import { catalogAt, LATEST_CATALOG_VERSION } from "../../../services/ais-api/src/docks/catalog.js";

const API = "/api/slip-inventory/v1";
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const fid = () => `DF-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
const r1 = (x) => Math.round(x * 10) / 10;

const STYLE = {
  "walkway-module": { fillColor: "#c9ae84", fillOpacity: 0.85, color: "#6b4f2e", weight: 1 },
  "corner-module": { fillColor: "#bfa276", fillOpacity: 0.85, color: "#6b4f2e", weight: 1 },
  "walkway-junction": { fillColor: "#bfa276", fillOpacity: 0.85, color: "#0891b2", weight: 1.5 },
  finger: { fillColor: "#c9ae84", fillOpacity: 0.85, color: "#6b4f2e", weight: 1 },
  "finger-frame": { fillColor: "#a8865a", fillOpacity: 0.85, color: "#6b4f2e", weight: 0.75 },
  pile: { fillColor: "#111827", fillOpacity: 1, color: "#111827", weight: 1 },
  "pile-guide": { fill: false, color: "#dc2626", weight: 1 },
  gangway: { fillColor: "#cbd5e1", fillOpacity: 0.9, color: "#334155", weight: 1 },
  "power-pedestal": { fillColor: "#f8fafc", fillOpacity: 1, color: "#0f172a", weight: 1 },
  "dock-box": { fillColor: "#e2e8f0", fillOpacity: 1, color: "#334155", weight: 1 },
  cleat: { fillColor: "#94a3b8", fillOpacity: 1, color: "#334155", weight: 0.75 },
  gate: { fillColor: "#fde68a", fillOpacity: 0.9, color: "#92400e", weight: 1.5 },
  "mooring-whip": { fillColor: "#e2e8f0", fillOpacity: 1, color: "#334155", weight: 0.75 },
  "fire-station": { fillColor: "#dc2626", fillOpacity: 1, color: "#7f1d1d", weight: 1 },
  "life-ring-station": { fillColor: "#f97316", fillOpacity: 1, color: "#7c2d12", weight: 1 },
  "fence-panel": { fill: false, color: "#64748b", weight: 1.5, dashArray: "2 2" },
  "security-light": { fillColor: "#fde68a", fillOpacity: 1, color: "#92400e", weight: 0.75 },
  "trash-receptacle": { fillColor: "#16a34a", fillOpacity: 1, color: "#14532d", weight: 0.75 },
};
const TOOL_HINT = {
  walkway: "Walkway: click points along the centreline; starting near another dock's edge makes a T or cross connection there. Enter or double-click finishes. Esc cancels.",
  fingers: "Fingers: click beside a walkway where the first finger goes, then where the last goes.",
  pile: "Pile: click to place one, or click an orange suggestion.",
  fitting: "Fitting: click on a dock to place the chosen fitting, or click a suggestion.",
  ramp: "Gangway: click near a dock's edge to add one, running 15 ft out to shore by default. Select it afterward to drag the shore end, or edit its width, rise and surface below.",
  select: "Select: click a walkway, finger, gangway or pile to edit it. Esc deselects.",
  pick: "Click the walkway to fill with fingers from its slips. Esc cancels.",
};
const PREVIEW = { color: "#38bdf8", weight: 1.5, dashArray: "4 3", fillColor: "#38bdf8", fillOpacity: 0.18 };

export function createDocksMode({ app, map, getMarinaId, getSlipRings, stopOtherTools, isOtherToolActive, requestRender }) {
  const s = {
    marinaId: "", loading: false, assemblies: [], assembly: null, features: new Map(), frame: null, built: null,
    tool: null, sel: null, status: "", error: "", show: true, active: true, suggestions: [], undo: [], redo: [], busy: false,
    opts: { walkWidth: null, fingerLength: null, fingerWidth: null, fingerSpacing: 19.9 },
  };
  const sys = () => catalogAt(s.assembly?.catalog_pin ?? null).systems[s.assembly?.system || "waler-concrete"];
  const def = (part, key) => sys().parts[part].params[key].default;
  const opt = (k, part, key) => (s.opts[k] != null ? s.opts[k] : def(part, key));

  // ── map layers ──
  // leaflet-rotate (core/map.js) splits the map pane into a rotating child (_rotatePane, home of
  // the basemap and the slips) and an upright one (_norotatePane, for markers). A pane created
  // without a parent lands on the unrotated map pane and stays north-up while the map turns, so
  // the docks go in the rotating pane and the drag handles (markers) in the upright one.
  if (!map.getPane("slipDocksPane")) { const el = map.createPane("slipDocksPane", map._rotatePane || undefined); el.style.zIndex = 445; el.style.pointerEvents = "none"; }
  if (!map.getPane("slipDocksHandles")) map.createPane("slipDocksHandles", map._norotatePane || undefined).style.zIndex = 650;
  const renderer = L.canvas({ pane: "slipDocksPane", padding: 0.3 });
  const layer = L.layerGroup().addTo(map), preview = L.layerGroup().addTo(map), handles = L.layerGroup().addTo(map);
  let label = null;

  const toLL = ([x, y]) => { const [lon, lat] = s.frame.toLonLat([x, y]); return [lat, lon]; };
  const ptOf = (e) => { const ll = map.mouseEventToLatLng(e); return { ll, local: s.frame ? s.frame.toLocal([ll.lng, ll.lat]) : null }; };
  const pxDist = (a, b) => map.latLngToContainerPoint(L.latLng(a)).distanceTo(map.latLngToContainerPoint(L.latLng(b)));

  function localFeatures(extra = []) {
    const out = [];
    for (const f of [...s.features.values(), ...extra]) {
      const g = f.geometry || {};
      out.push({ id: f.feature_id, kind: f.kind, params: f.params || {},
        geometry: g.points ? { points: g.points.map((p) => s.frame.toLocal(p)) } : g.point ? { point: s.frame.toLocal(g.point) } : {} });
    }
    return out;
  }
  function rebuild() {
    if (!s.frame) { s.built = null; return; }
    try { s.built = buildAssembly({ features: localFeatures(), system: s.assembly?.system, catalogVersion: s.assembly?.catalog_pin ?? null, rules: s.assembly?.rules || {} }); s.error = ""; }
    catch (err) { s.built = null; s.error = String(err?.message || err); }
  }
  function drawComponents(group, built, style = null, selected = null) {
    for (const c of built.components) {
      const st = style || STYLE[c.part] || STYLE["walkway-module"];
      const hi = selected && c.feature === selected ? { color: "#22d3ee", weight: 2.5 } : {};
      for (const poly of c.footprints || [c.footprint]) L.polygon(poly.map(toLL), { ...st, ...hi, renderer, interactive: false }).addTo(group);
    }
  }
  function drawAll() {
    layer.clearLayers();
    if (!s.active) { handles.clearLayers(); preview.clearLayers(); return; }
    if (!s.show || !s.built) return;
    drawComponents(layer, s.built, null, s.sel);
    for (const p of s.suggestions) L.circleMarker(toLL(p.at), { radius: p.type === "cleat" ? 3 : 5, color: p.type ? "#38bdf8" : "#f59e0b", weight: 2, dashArray: "2 2", fillOpacity: 0.15, renderer, interactive: false }).addTo(layer);
    drawHandles();
  }
  function setLabel(latlng, text) {
    if (!text) { if (label) { map.removeLayer(label); label = null; } return; }
    const icon = L.divIcon({ className: "", html: `<div style="transform:translate(14px,-28px);white-space:nowrap;background:rgba(15,23,42,.92);color:#e2e8f0;border:1px solid rgba(56,189,248,.6);border-radius:6px;padding:2px 7px;font:600 12px system-ui;">${esc(text)}</div>`, iconSize: [0, 0] });
    if (!label) label = L.marker(latlng, { icon, interactive: false, pane: "tooltipPane" }).addTo(map);
    else { label.setLatLng(latlng); label.setIcon(icon); }
  }

  // ── server ──
  async function api(method, path, body) {
    const r = await fetch(API + path, { method, headers: { "content-type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
    const j = await r.json().catch(() => ({ ok: false, error: `HTTP ${r.status}` }));
    if (!r.ok || !j.ok) throw Object.assign(new Error(j.error || `HTTP ${r.status}`), { status: r.status, body: j });
    return j;
  }
  async function loadMarina(marinaId) {
    s.marinaId = marinaId; s.assemblies = []; s.assembly = null; s.features = new Map(); s.frame = null; s.built = null;
    s.sel = null; s.suggestions = []; s.undo = []; s.redo = []; stopTool(); drawAll();
    if (!marinaId) { requestRender(); return; }
    s.loading = true; requestRender();
    try {
      const j = await api("GET", `/dock-assemblies?marina_id=${encodeURIComponent(marinaId)}`);
      if (s.marinaId !== marinaId) return;
      s.assemblies = j.items || [];
      if (s.assemblies.length) await openAssembly(s.assemblies[0].assembly_id);
    } catch (err) { s.error = `Couldn't load docks: ${err.message}`; }
    finally { s.loading = false; requestRender(); }
  }
  async function openAssembly(id) {
    const j = await api("GET", `/dock-assemblies/${encodeURIComponent(id)}`);
    s.assembly = j.item; s.features = new Map(j.features.map((f) => [f.feature_id, f]));
    s.frame = s.assembly.origin_lon != null ? localFrame(s.assembly.origin_lon, s.assembly.origin_lat) : null;
    s.sel = null; s.suggestions = []; s.undo = []; s.redo = [];
    rebuild(); drawAll(); requestRender();
  }
  async function ensureAssembly(lonlat) {
    if (s.assembly) return;
    const j = await api("POST", "/dock-assemblies", { marina_id: s.marinaId, name: "Docks", origin_lon: lonlat[0], origin_lat: lonlat[1], created_by_email: app?.auth?.email || null });
    s.assemblies = [...s.assemblies, j.item]; s.assembly = j.item; s.frame = localFrame(j.item.origin_lon, j.item.origin_lat);
  }

  // One edit = ops sent atomically + their inverse kept for undo. Creates carry client ids so the
  // inverse is known before the server answers.
  function inverseOf(ops) {
    return ops.slice().reverse().map((op) => {
      if (op.op === "create") return { op: "delete", feature_id: op.feature_id };
      const b = s.features.get(op.feature_id);
      if (op.op === "delete") return { op: "create", feature_id: b.feature_id, kind: b.kind, geometry: b.geometry, params: b.params };
      return { op: "update", feature_id: b.feature_id, geometry: b.geometry, params: b.params };
    });
  }
  async function commit(label, ops, { record = true } = {}) {
    if (!ops.length || s.busy) return false;
    ops = ops.map((o) => (o.op === "create" && !o.feature_id ? { ...o, feature_id: fid() } : o));
    const inverse = inverseOf(ops);
    s.busy = true;
    try {
      const j = await api("POST", `/dock-assemblies/${encodeURIComponent(s.assembly.assembly_id)}/edits`, { ops, label, actor_email: app?.auth?.email || null, expected_rev: s.assembly.rev });
      s.assembly = { ...s.assembly, rev: j.rev };
      for (const f of j.features) { if (f.deleted) s.features.delete(f.feature_id); else s.features.set(f.feature_id, f); }
      if (record) { s.undo.push({ label, ops, inverse }); s.redo = []; }
      if (s.sel && !s.features.has(s.sel)) s.sel = null;
      s.status = label; s.error = "";
      rebuild(); drawAll(); requestRender();
      return true;
    } catch (err) {
      if (err.status === 409) { s.error = "Someone else changed these docks; reloaded the latest."; await openAssembly(s.assembly.assembly_id); }
      else s.error = `Not saved: ${err.message}`;
      requestRender();
      return false;
    } finally { s.busy = false; }
  }
  // the stacks change after the save, so the panel is redrawn again once they're settled
  async function undo() { const e = s.undo.pop(); if (!e) return; if (await commit(`Undo: ${e.label}`, e.inverse, { record: false })) s.redo.push(e); else s.undo.push(e); requestRender(); }
  async function redo() { const e = s.redo.pop(); if (!e) return; if (await commit(e.label, e.ops, { record: false })) s.undo.push(e); else s.redo.push(e); requestRender(); }

  // ── tools ──
  let off = null, tool = null; // tool = per-tool state + handlers
  function stopTool() {
    if (off) { off(); off = null; }
    tool = null; s.tool = null; preview.clearLayers(); handles.clearLayers(); setLabel(null);
    try { map.doubleClickZoom.enable(); } catch {}
    try { map.getContainer().classList.remove("slip-free-draw-cursor"); } catch {}
  }
  function startTool(name) {
    stopOtherTools?.();
    stopTool();
    s.tool = name; s.status = ""; s.error = "";
    tool = TOOLS[name]();
    const el = map.getContainer();
    el.classList.add("slip-free-draw-cursor");
    map.doubleClickZoom.disable();
    let down = null;
    const typing = (e) => /^(input|textarea|select)$/i.test(e.target?.tagName || "") || e.target?.isContentEditable;
    const onDown = (e) => { if (e.button == null || e.button === 0) down = { x: e.clientX, y: e.clientY }; };
    const onMove = (e) => { if (s.frame || tool.noFrameOk) tool.move?.(ptOf(e), e); };
    const onUp = (e) => {
      if (!down) return; const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y) > 6; down = null;
      if (!moved) tool.click?.(ptOf(e), e);
    };
    const swallow = (e) => { e.stopPropagation(); e.preventDefault(); };
    const onDbl = (e) => { swallow(e); tool.finish?.(); };
    const onKey = (e) => {
      if (typing(e)) return;
      if (e.key === "Escape") { e.preventDefault(); if (!tool.cancel?.()) { stopTool(); requestRender(); } return; }
      tool.key?.(e);
    };
    el.addEventListener("pointerdown", onDown, true); el.addEventListener("pointermove", onMove, true); el.addEventListener("pointerup", onUp, true);
    el.addEventListener("click", swallow, true); el.addEventListener("dblclick", onDbl, true); document.addEventListener("keydown", onKey);
    off = () => {
      el.removeEventListener("pointerdown", onDown, true); el.removeEventListener("pointermove", onMove, true); el.removeEventListener("pointerup", onUp, true);
      el.removeEventListener("click", swallow, true); el.removeEventListener("dblclick", onDbl, true); document.removeEventListener("keydown", onKey);
    };
    drawAll(); requestRender();
  }
  // digits typed while drawing: a length or a count, shown in the label, applied with Enter
  const typedKey = (t, e) => {
    if (/^[0-9.]$/.test(e.key)) { t.typed = (t.typed || "") + e.key; e.preventDefault(); return true; }
    if (e.key === "Backspace" && t.typed) { t.typed = t.typed.slice(0, -1); e.preventDefault(); return true; }
    return false;
  };

  const walkways = () => [...s.features.values()].filter((f) => f.kind === "walkway");
  // nearest walkway to a local point: its feature, station, offset, and how far away in ft
  function nearestWalkway(p, maxFt = 60) {
    let best = null;
    for (const w of walkways()) {
      const pr = projectOnWalkway(w.geometry.points.map((q) => s.frame.toLocal(q)), p);
      if (pr && pr.dist <= maxFt && (!best || pr.dist < best.pr.dist)) best = { w, pr };
    }
    return best;
  }
  // The point on a walkway's edge nearest a local point: station, which side, the outward normal,
  // and the exact edge point there -- used both for T/cross junction snapping (a new walkway
  // starting at another one's edge) and for a gangway's dock-end root.
  function walkwayEdgeAt(w, local) {
    const pts = w.geometry.points.map((q) => s.frame.toLocal(q));
    const pr = projectOnWalkway(pts, local);
    if (!pr) return null;
    const a = pts[pr.seg], b = pts[pr.seg + 1], d = [b[0] - a[0], b[1] - a[1]], L0 = Math.hypot(...d) || 1;
    const u = [d[0] / L0, d[1] / L0], side = pr.offset >= 0 ? 1 : -1, n = [-u[1] * side, u[0] * side];
    const t = pr.station - pr.segStart, center = [a[0] + u[0] * t, a[1] + u[1] * t];
    const halfW = (Number(w.params?.width) || def("walkway-module", "width")) / 2;
    return { station: pr.station, side, normal: n, total: pr.total, point: [center[0] + n[0] * halfW, center[1] + n[1] * halfW] };
  }

  // The dock part under a map point, smallest part first (a pile over its guide over a finger ...).
  function hitComponent(ll) {
    if (!s.built || !s.frame) return null;
    const q = map.latLngToContainerPoint(ll);
    let best = null;
    for (const c of s.built.components) for (const poly of c.footprints || [c.footprint]) {
      const pts = poly.map(toLL).map((x) => map.latLngToContainerPoint(L.latLng(x)));
      let inside = false;
      for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) if ((pts[i].y > q.y) !== (pts[j].y > q.y) && q.x < ((pts[j].x - pts[i].x) * (q.y - pts[i].y)) / (pts[j].y - pts[i].y) + pts[i].x) inside = !inside;
      const rank = ["pile", "pile-guide", "power-pedestal", "dock-box", "cleat", "gate", "mooring-whip", "fire-station", "life-ring-station", "fence-panel", "security-light", "trash-receptacle"].includes(c.part) ? 0 : c.part === "finger-frame" ? 1 : c.part === "finger" || c.part === "gangway" ? 2 : 3;
      if (inside && (!best || rank < best.rank)) best = { c, rank };
    }
    return best?.c || null;
  }
  const hit = (pt) => hitComponent(pt.ll);

  const TOOLS = {
    walkway: () => {
      const t = { pts: [], hover: null, noFrameOk: true, attach: null };
      const width = () => opt("walkWidth", "walkway-module", "width");
      const snap = (pt, e) => {
        if (!s.frame) return pt.local || null;
        if (!t.pts.length) { // the first point: snap to another walkway's edge for a T/cross junction
          t.attach = null;
          for (const w of walkways()) {
            const edge = walkwayEdgeAt(w, pt.local);
            if (edge && edge.station > 0.5 && edge.station < edge.total - 0.5 && Math.hypot(edge.point[0] - pt.local[0], edge.point[1] - pt.local[1]) < 10) {
              t.attach = { walkway: w.feature_id, station: edge.station, normal: edge.normal };
              return edge.point;
            }
          }
          return pt.local;
        }
        for (const w of walkways()) for (const q of [w.geometry.points[0], w.geometry.points.at(-1)]) {
          if (pxDist([q[1], q[0]], pt.ll) < 12) return s.frame.toLocal(q);
        }
        let p = pt.local;
        if (e?.shiftKey && t.pts.length) { // 15-degree steps
          const a = t.pts.at(-1), d = [p[0] - a[0], p[1] - a[1]], L0 = Math.hypot(...d), ang = Math.round(Math.atan2(d[1], d[0]) / (Math.PI / 12)) * (Math.PI / 12);
          p = [a[0] + L0 * Math.cos(ang), a[1] + L0 * Math.sin(ang)];
        }
        return p;
      };
      const draw = () => {
        preview.clearLayers(); if (!s.frame) return;
        const pts = t.hover ? [...t.pts, t.hover] : t.pts;
        if (pts.length >= 2) {
          try { drawComponents(preview, buildAssembly({ features: [{ id: "preview", kind: "walkway", geometry: { points: pts }, params: { width: width() } }], system: s.assembly?.system, catalogVersion: s.assembly?.catalog_pin ?? null }), PREVIEW); } catch {}
          L.polyline(pts.map(toLL), { color: "#38bdf8", weight: 1, renderer, interactive: false }).addTo(preview);
        }
        if (t.hover && t.pts.length) {
          const a = t.pts.at(-1), L0 = Math.hypot(t.hover[0] - a[0], t.hover[1] - a[1]);
          setLabel(toLL(t.hover), t.typed ? `${t.typed} ft, Enter` : `${r1(L0)} ft · ${width()} ft wide`);
        } else if (t.hover && t.attach) setLabel(toLL(t.hover), "T/cross junction here");
        else setLabel(null);
      };
      const add = (p) => { t.pts.push(p); t.typed = ""; draw(); };
      t.move = (pt, e) => {
        if (!s.frame) { t.hoverLL = pt.ll; return; }
        t.hover = snap(pt, e); draw();
      };
      t.click = async (pt, e) => {
        if (!s.frame) { s.frame = localFrame(pt.ll.lng, pt.ll.lat); t.tempFrame = true; } // first point of a marina's first dock
        add(snap({ ll: pt.ll, local: s.frame.toLocal([pt.ll.lng, pt.ll.lat]) }, e));
        s.status = t.pts.length < 2 ? "Walkway: click the next point" : "Walkway: click more points, type a length + Enter, or Enter / double-click to finish";
        requestRender();
      };
      t.finish = async () => {
        if (t.pts.length < 2) return;
        const pts = t.pts.map((p) => s.frame.toLonLat(p));
        const attach = t.attach ? { walkway: t.attach.walkway, station: t.attach.station } : null;
        await ensureAssembly(pts[0]);
        if (t.tempFrame) s.frame = localFrame(s.assembly.origin_lon, s.assembly.origin_lat);
        t.pts = []; t.hover = null; t.tempFrame = false; t.attach = null; preview.clearLayers(); setLabel(null);
        const id = fid();
        const params = { width: width(), ...(attach ? { attach } : {}) };
        if (await commit(attach ? "Walkway (junction)" : "Walkway", [{ op: "create", feature_id: id, kind: "walkway", geometry: { points: pts }, params }])) {
          startTool("select"); s.sel = id; s.status = "Walkway saved and selected. From slips adds its fingers; Walkway draws another."; drawAll(); requestRender();
        }
      };
      t.key = (e) => {
        if (typedKey(t, e)) { draw(); return; }
        if (e.key === "Enter") {
          e.preventDefault();
          if (t.typed && t.pts.length && t.hover) { // exact length along the pointer's direction
            const a = t.pts.at(-1), d = [t.hover[0] - a[0], t.hover[1] - a[1]], L0 = Math.hypot(...d) || 1, n = Number(t.typed);
            if (n > 0) add([a[0] + (d[0] / L0) * n, a[1] + (d[1] / L0) * n]);
          } else t.finish();
        } else if (e.key === "Backspace" && t.pts.length) { e.preventDefault(); t.pts.pop(); draw(); }
      };
      t.cancel = () => { if (!t.pts.length) return false; t.pts = []; t.typed = ""; t.attach = null; preview.clearLayers(); setLabel(null); if (t.tempFrame && !s.assembly) s.frame = null; return true; };
      return t;
    },

    fingers: () => {
      const t = { start: null, end: null, count: null };
      const len = () => opt("fingerLength", "finger", "length"), wid = () => opt("fingerWidth", "finger", "width");
      const plan = () => {
        if (!t.start) return [];
        const s0 = t.start.station, s1 = t.end ? t.end.station : s0;
        const n = t.count ?? Math.max(1, Math.round(Math.abs(s1 - s0) / s.opts.fingerSpacing) + 1);
        return Array.from({ length: n }, (_, i) => (n === 1 ? s0 : s0 + ((s1 - s0) * i) / (n - 1)));
      };
      const draw = (hoverHit) => {
        preview.clearLayers();
        const hit = t.start || hoverHit; if (!hit) { setLabel(null); return; }
        const stations = t.start ? plan() : [hit.station];
        const feats = [...localFeatures().filter((f) => f.id === hit.w.feature_id), ...stations.map((st, i) => ({ id: `p${i}`, kind: "finger", params: { walkway: hit.w.feature_id, station: st, side: hit.side, length: len(), width: wid() } }))];
        try { drawComponents(preview, { components: buildAssembly({ features: feats, system: s.assembly?.system, catalogVersion: s.assembly?.catalog_pin ?? null }).components.filter((c) => c.feature !== hit.w.feature_id) }, PREVIEW); } catch {}
        const n = stations.length, sp = n > 1 ? Math.abs(stations[1] - stations[0]) : 0;
        setLabel(t.lastLL, t.typed ? `${t.typed} fingers, Enter` : t.start ? `${n} finger${n === 1 ? "" : "s"}${n > 1 ? ` · ${r1(sp)} ft apart` : ""} · ${len()} x ${wid()} ft` : `${len()} x ${wid()} ft finger`);
      };
      const hitAt = (p) => { const h = nearestWalkway(p); return h ? { w: h.w, station: h.pr.station, side: h.pr.offset >= 0 ? 1 : -1 } : null; };
      t.move = (pt) => {
        t.lastLL = pt.ll;
        if (!t.start) { draw(hitAt(pt.local)); return; }
        const pr = projectOnWalkway(t.start.w.geometry.points.map((q) => s.frame.toLocal(q)), pt.local);
        t.end = { station: pr.station }; draw();
      };
      t.click = async () => {
        if (!t.start) {
          const hit = t.lastLL && hitAt(s.frame.toLocal([t.lastLL.lng, t.lastLL.lat]));
          if (!hit) { s.error = "Click next to a walkway."; requestRender(); return; }
          t.start = hit; t.end = { station: hit.station }; s.status = "Fingers: move along the walkway and click the last finger; + / - or type a count + Enter"; s.error = ""; draw(); requestRender();
          return;
        }
        const st = t.start, stations = plan();
        t.start = null; t.end = null; t.count = null; t.typed = ""; preview.clearLayers(); setLabel(null);
        await commit(`${stations.length} finger${stations.length === 1 ? "" : "s"}`, stations.map((station) => ({ op: "create", kind: "finger", params: { walkway: st.w.feature_id, station: Math.round(station * 100) / 100, side: st.side, length: len(), width: wid() } })));
      };
      t.key = (e) => {
        if (!t.start) return;
        if (typedKey(t, e)) { draw(); return; }
        if (e.key === "+" || e.key === "=") { t.count = plan().length + 1; draw(); }
        else if (e.key === "-" && plan().length > 1) { t.count = plan().length - 1; draw(); }
        else if (e.key === "Enter") { e.preventDefault(); const n = parseInt(t.typed, 10); if (n > 0) t.count = n; t.typed = ""; draw(); }
      };
      t.cancel = () => { if (!t.start) return false; t.start = null; t.end = null; t.count = null; t.typed = ""; preview.clearLayers(); setLabel(null); return true; };
      return t;
    },

    pile: () => {
      const t = {};
      t.move = (pt) => {
        const near = s.suggestions.find((q) => !q.type && pxDist(toLL(q.at), pt.ll) < 10);
        setLabel(pt.ll, near ? `Add suggested pile (${near.reason})` : "Click to place a pile");
      };
      t.click = async (pt) => {
        await ensureAssembly([pt.ll.lng, pt.ll.lat]);
        const near = s.suggestions.find((q) => !q.type && pxDist(toLL(q.at), pt.ll) < 10);
        const at = near ? s.frame.toLonLat(near.at) : [pt.ll.lng, pt.ll.lat];
        if (near) s.suggestions = s.suggestions.filter((q) => q !== near);
        await commit("Pile", [{ op: "create", kind: "pile", geometry: { point: at }, params: {} }]);
      };
      return t;
    },

    ramp: () => {
      const t = {};
      const edgeNear = (local) => {
        for (const w of walkways()) {
          const edge = walkwayEdgeAt(w, local);
          if (edge && edge.station > 0.1 && edge.station < edge.total - 0.1 && Math.hypot(edge.point[0] - local[0], edge.point[1] - local[1]) < 15) return { w, edge };
        }
        return null;
      };
      t.move = (pt) => {
        if (!s.frame) { setLabel(pt.ll, "Draw a walkway first"); return; }
        setLabel(pt.ll, edgeNear(pt.local) ? "Click to add a gangway here, 15 ft out to shore" : "Click near a dock's edge to add a gangway");
      };
      t.click = async (pt) => {
        if (!s.frame) { s.error = "Draw a walkway first."; requestRender(); return; }
        const near = edgeNear(pt.local);
        if (!near) { s.error = "Click near a dock's edge."; requestRender(); return; }
        const { w, edge } = near;
        const shore = s.frame.toLonLat([edge.point[0] + edge.normal[0] * 15, edge.point[1] + edge.normal[1] * 15]);
        await commit("Gangway", [{ op: "create", kind: "ramp", geometry: { point: shore }, params: { walkway: w.feature_id, station: Math.round(edge.station * 100) / 100 } }]);
      };
      return t;
    },

    fitting: () => {
      const t = {}, type = () => s.opts.fitType || "power-pedestal", label = (ty) => sys().parts[ty]?.label || ty;
      t.move = (pt) => {
        const near = s.suggestions.find((q) => q.type && pxDist(toLL(q.at), pt.ll) < 10);
        setLabel(pt.ll, near ? `Add suggested ${label(near.type).toLowerCase()}` : `Click on a dock to place a ${label(type()).toLowerCase()}`);
      };
      t.click = async (pt) => {
        const near = s.suggestions.find((q) => q.type && pxDist(toLL(q.at), pt.ll) < 10);
        const ty = near ? near.type : type(), at = near ? s.frame.toLonLat(near.at) : [pt.ll.lng, pt.ll.lat];
        if (near) s.suggestions = s.suggestions.filter((q) => q !== near);
        await commit(label(ty), [{ op: "create", kind: "accessory", geometry: { point: at }, params: { type: ty } }]);
      };
      return t;
    },

    // one click on (or near) a walkway, then From slips runs on it
    pick: () => ({
      click: async (pt) => {
        const h = nearestWalkway(pt.local, 30);
        if (!h) { s.error = "That's not on a walkway. Click a walkway, or Esc."; requestRender(); return; }
        s.sel = h.w.feature_id; s.error = ""; stopTool(); startTool("select"); s.sel = h.w.feature_id; drawAll();
        await fingersFromSlipsForSelected();
      },
      move: (pt) => setLabel(pt.ll, nearestWalkway(pt.local, 30) ? "Click to fill this walkway from its slips" : "Click a walkway"),
    }),

    select: () => {
      const t = {};
      const nearWalk = (pt) => {
        const h = nearestWalkway(pt.local, 60); if (!h) return null;
        const halfFt = (Number(h.w.params?.width) || def("walkway-module", "width")) / 2, ftPerPx = map.containerPointToLatLng([0, 0]).distanceTo(map.containerPointToLatLng([0, 1])) * 3.280839895;
        return h.pr.dist <= halfFt + 12 * ftPerPx ? h.w : null;
      };
      t.click = (pt) => {
        const c = hit(pt), w = c ? null : nearWalk(pt);
        s.sel = c ? c.feature : w ? w.feature_id : null;
        s.status = s.sel ? "" : "Nothing there. Click a walkway, finger or pile.";
        drawAll(); requestRender();
      };
      t.key = (e) => { if ((e.key === "Delete" || e.key === "Backspace") && s.sel) { e.preventDefault(); deleteSelected(); } };
      t.cancel = () => { if (!s.sel) return false; s.sel = null; drawAll(); requestRender(); return true; };
      return t;
    },
  };

  // Draggable vertices of the selected walkway, or the shore end of a selected gangway (Select tool).
  function drawHandles() {
    handles.clearLayers();
    const f = s.sel && s.features.get(s.sel);
    if (!f || s.tool !== "select") return;
    const handleIcon = L.divIcon({ className: "", html: '<div style="width:12px;height:12px;margin:-6px 0 0 -6px;border-radius:50%;background:#22d3ee;border:2px solid #0f172a;cursor:move"></div>', iconSize: [0, 0] });
    if (f.kind === "walkway") {
      f.geometry.points.forEach((p, i) => {
        const m = L.marker([p[1], p[0]], { draggable: true, pane: "slipDocksHandles", icon: handleIcon }).addTo(handles);
        const moved = () => { const ll = m.getLatLng(); const pts = f.geometry.points.map((q, k) => (k === i ? [ll.lng, ll.lat] : q)); return pts; };
        m.on("drag", () => {
          preview.clearLayers();
          const pts = moved().map((q) => s.frame.toLocal(q));
          try { drawComponents(preview, buildAssembly({ features: localFeatures().map((x) => (x.id === f.feature_id ? { ...x, geometry: { points: pts } } : x)), system: s.assembly.system, catalogVersion: s.assembly.catalog_pin ?? null }), PREVIEW); } catch {}
        });
        m.on("dragend", async () => { preview.clearLayers(); await commit("Move walkway point", [{ op: "update", feature_id: f.feature_id, rev: f.rev, geometry: { points: moved() } }]); });
      });
    } else if (f.kind === "ramp" && f.geometry?.point) {
      const p = f.geometry.point;
      const m = L.marker([p[1], p[0]], { draggable: true, pane: "slipDocksHandles", icon: handleIcon }).addTo(handles);
      m.on("drag", () => {
        preview.clearLayers();
        const ll = m.getLatLng(), pt = s.frame.toLocal([ll.lng, ll.lat]);
        try { drawComponents(preview, buildAssembly({ features: localFeatures().map((x) => (x.id === f.feature_id ? { ...x, geometry: { point: pt } } : x)), system: s.assembly.system, catalogVersion: s.assembly.catalog_pin ?? null }), PREVIEW); } catch {}
      });
      m.on("dragend", async () => { preview.clearLayers(); const ll = m.getLatLng(); await commit("Move gangway shore point", [{ op: "update", feature_id: f.feature_id, rev: f.rev, geometry: { point: [ll.lng, ll.lat] } }]); });
    }
  }

  async function deleteSelected() {
    const f = s.features.get(s.sel); if (!f) return;
    const ops = [{ op: "delete", feature_id: f.feature_id }];
    if (f.kind === "walkway") for (const g of s.features.values()) {
      if (g.kind === "finger" && g.params?.walkway === f.feature_id) ops.push({ op: "delete", feature_id: g.feature_id });
      else if (g.kind === "ramp" && g.params?.walkway === f.feature_id) ops.push({ op: "delete", feature_id: g.feature_id });
      else if (g.kind === "walkway" && g.params?.attach?.walkway === f.feature_id) ops.push({ op: "delete", feature_id: g.feature_id });
    }
    s.sel = null;
    await commit(f.kind === "walkway" && ops.length > 1 ? `Delete walkway and its ${ops.length - 1} attachments` : `Delete ${f.kind}`, ops);
  }
  async function fingersFromSlipsForSelected() {
    const w = s.sel && s.features.get(s.sel);
    const walk = w?.kind === "walkway" ? w : walkways().length === 1 ? walkways()[0] : null;
    if (!walk) {
      if (!walkways().length) { s.error = "Draw a walkway first."; requestRender(); return; }
      startTool("pick"); s.status = "Click the walkway to fill with fingers from its slips."; requestRender(); return;
    }
    const pts = walk.geometry.points.map((q) => s.frame.toLocal(q));
    const rings = (getSlipRings() || []).map((ring) => ring.map(([lat, lng]) => s.frame.toLocal([lng, lat])));
    const have = [...s.features.values()].filter((g) => g.kind === "finger" && g.params?.walkway === walk.feature_id);
    const ops = [];
    for (const side of [1, -1]) {
      for (const f of fingersFromSlips(pts, Number(walk.params?.width) || def("walkway-module", "width"), side, rings)) {
        if (have.some((g) => Number(g.params.side) === side && Math.abs(Number(g.params.station) - f.station) < 1.5)) continue;
        ops.push({ op: "create", kind: "finger", params: { walkway: walk.feature_id, station: f.station, side, length: f.length, width: opt("fingerWidth", "finger", "width") } });
      }
    }
    if (!ops.length) { s.status = "No new fingers: none found along this walkway's slips, or they're already drawn."; requestRender(); return; }
    await commit(`${ops.length} fingers from slips`, ops);
  }
  function suggest() {
    if (!s.built) return;
    const piles = s.built.components.filter((c) => c.part === "pile");
    s.suggestions = suggestPiles(s.built, { walkSpacing: sys().rules.autoPiles.walkSpacing, size: def("pile", "size") })
      .filter((q) => !piles.some((p) => Math.hypot(p.center[0] - q.at[0], p.center[1] - q.at[1]) < 4));
    s.status = s.suggestions.length ? `${s.suggestions.length} suggested piles (orange). Pile tool: click one to add it, or Add all.` : "No piles to suggest.";
    drawAll(); requestRender();
  }
  function suggestFit() {
    if (!s.built) return;
    s.suggestions = suggestFittings(s.built);
    const n = (ty) => s.suggestions.filter((q) => q.type === ty).length;
    s.status = s.suggestions.length ? `Suggested ${n("power-pedestal")} power posts and ${n("cleat")} cleats (blue). Fitting tool: click one to add it, or Add all.` : "No fittings to suggest.";
    drawAll(); requestRender();
  }
  async function addAllSuggestions() {
    const ops = s.suggestions.map((q) => (q.type
      ? { op: "create", kind: "accessory", geometry: { point: s.frame.toLonLat(q.at) }, params: { type: q.type } }
      : { op: "create", kind: "pile", geometry: { point: s.frame.toLonLat(q.at) }, params: {} }));
    const what = s.suggestions.some((q) => q.type) ? "fittings" : "piles";
    s.suggestions = [];
    await commit(`${ops.length} ${what}`, ops);
  }
  async function updateSelected(patch) {
    const f = s.features.get(s.sel); if (!f) return;
    await commit(`Edit ${f.kind}`, [{ op: "update", feature_id: f.feature_id, rev: f.rev, params: { ...f.params, ...patch } }]);
  }

  // ── panel (re-rendered into the slip tool's panel on every slip-tool render) ──
  function editorHtml() {
    const f = s.sel && s.features.get(s.sel); if (!f) return "";
    const part = { walkway: "walkway-module", finger: "finger", pile: "pile", ramp: "gangway", accessory: f.params?.type }[f.kind];
    const fields = { walkway: ["width"], finger: ["length", "width", "tip", "station"], pile: ["size", "section", "topElev", "tipElev"], ramp: ["width", "shoreRise", "handrailHeight", "surface"], accessory: [...Object.keys(sys().parts[f.params?.type]?.params || {}), "rotation"] }[f.kind] || [];
    const P = part && sys().parts[part] ? sys().parts[part].params : {};
    const input = (k) => {
      const d = P[k] || (k === "rotation" ? { label: "Rotation", unit: "deg", step: 5 } : { label: "Station along walkway", unit: "ft", step: 0.1 });
      const v = f.params?.[k] ?? d.default ?? "";
      if (d.options) return `<label style="display:flex;justify-content:space-between;gap:8px;">${esc(d.label)}<select data-dock-param="${k}">${d.options.map((o) => `<option ${o === v ? "selected" : ""}>${esc(String(o))}</option>`).join("")}</select></label>`;
      return `<label style="display:flex;justify-content:space-between;gap:8px;">${esc(d.label)}${d.approx ? " <span title='Approximate: not published by manufacturers, measured or estimated' style='color:#f59e0b'>~</span>" : ""}<span><input data-dock-param="${k}" type="number" step="${d.step ?? 0.1}" value="${esc(v ?? "")}" style="width:72px;"> ${esc(d.unit || "")}</span></label>`;
    };
    const title = f.kind === "accessory" ? sys().parts[f.params?.type]?.label || "Fitting" : f.kind === "ramp" ? "Gangway" : f.kind[0].toUpperCase() + f.kind.slice(1);
    return `<div style="border:1px solid rgba(34,211,238,.4);border-radius:8px;padding:6px 8px;display:grid;gap:4px;">
      <div style="display:flex;justify-content:space-between;"><strong>${esc(title)}</strong><button type="button" class="btn" data-dock="delete" style="padding:1px 8px;">Delete</button></div>
      ${fields.map(input).join("")}</div>`;
  }
  function panelHtml() {
    if (!s.marinaId) return `<div class="small" style="color:var(--muted)">Choose a marina to draw its docks.</div>`;
    const b = s.built, count = (p) => (b ? b.components.filter((c) => c.part === p).length : 0);
    const failing = b ? b.checks.filter((c) => !c.ok) : [];
    const btn = (id, label, title, on) => `<button type="button" class="btn ${on ? "primary" : ""}" data-dock="${id}" title="${esc(title)}" style="min-height:30px;padding:3px 8px;border-radius:15px;${on ? "background:rgba(34,197,94,.2);border-color:#22c55e;" : ""}">${label}</button>`;
    const num = (k, part, key, labelText) => `<label style="display:flex;justify-content:space-between;gap:6px;">${labelText}<span><input data-dock-opt="${k}" type="number" step="0.1" value="${esc(opt(k, part, key))}" style="width:64px;"> ft</span></label>`;
    const rel = catalogAt(s.assembly?.catalog_pin ?? null);
    return `<div style="display:grid;gap:8px;">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
        <div><strong>Docks</strong> <span class="small" style="color:var(--muted)">${esc(sys().label)} · catalog v${rel.version}${s.assembly?.catalog_pin != null ? " (pinned)" : LATEST_CATALOG_VERSION === rel.version ? "" : ""}</span></div>
        <label class="small" style="display:flex;gap:4px;align-items:center;"><input type="checkbox" data-dock="show" ${s.show ? "checked" : ""}>Show</label>
      </div>
      ${s.assemblies.length > 1 ? `<select data-dock="assembly">${s.assemblies.map((a) => `<option value="${esc(a.assembly_id)}" ${a.assembly_id === s.assembly?.assembly_id ? "selected" : ""}>${esc(a.name)}</option>`).join("")}</select>` : s.assembly ? `<div class="small" style="color:var(--muted)">${esc(s.assembly.name)}</div>` : ""}
      <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;">
        ${btn("walkway", "Walkway", "Click points along the walkway's centreline. Starting near another dock's edge makes a T or cross connection there. Type a length + Enter for an exact segment; Shift snaps to 15 degrees; Enter or double-click finishes.", s.tool === "walkway")}
        ${btn("fingers", "Fingers", "Click beside a walkway where the first finger goes, then where the last one goes. + / - or a typed count + Enter sets how many.", s.tool === "fingers")}
        ${btn("pile", "Pile", "Click to place a pile; click an orange suggestion to add it.", s.tool === "pile")}
        ${btn("select", "Select", "Click a part to edit it; drag a walkway's points or a gangway's shore end; Delete removes it.", s.tool === "select")}
        ${btn("from-slips", "From slips", "Add fingers on the selected walkway from the slips along it.", false)}
        ${btn("suggest", "Suggest piles", "Propose piles at finger tips and along walkways.", false)}
        ${btn("fitting", "Fitting", "Place the chosen fitting on a dock (choose the type below), or a gate spanning the full width.", s.tool === "fitting")}
        ${btn("suggest-fit", "Suggest fittings", "Propose a power post at each finger root and cleats along each finger.", false)}
        ${btn("ramp", "Gangway", "Click near a dock's edge to add a gangway, 15 ft out to shore by default. Select it afterward to drag the shore end.", s.tool === "ramp")}
      </div>
      <div style="display:grid;gap:3px;font-size:12px;">
        ${num("walkWidth", "walkway-module", "width", "Walkway width")}
        ${num("fingerLength", "finger", "length", "Finger length")}
        ${num("fingerWidth", "finger", "width", "Finger width")}
        <label style="display:flex;justify-content:space-between;gap:6px;">Fitting<select data-dock-opt="fitType">${["power-pedestal", "dock-box", "cleat", "gate", "mooring-whip", "fire-station", "life-ring-station", "fence-panel", "security-light", "trash-receptacle"].map((k) => `<option value="${k}" ${(s.opts.fitType || "power-pedestal") === k ? "selected" : ""}>${esc(sys().parts[k]?.label || k)}</option>`).join("")}</select></label>
      </div>
      ${editorHtml()}
      ${s.suggestions.length ? `<button type="button" class="btn" data-dock="add-all">Add all ${s.suggestions.length} suggested ${s.suggestions.some((q) => q.type) ? "fittings" : "piles"}</button>` : ""}
      <div style="display:flex;gap:6px;">
        <button type="button" class="btn" data-dock="undo" ${s.undo.length ? "" : "disabled"} title="Ctrl+Z">Undo</button>
        <button type="button" class="btn" data-dock="redo" ${s.redo.length ? "" : "disabled"} title="Ctrl+Shift+Z">Redo</button>
        ${s.tool ? `<button type="button" class="btn" data-dock="done">Done (Esc)</button>` : ""}
      </div>
      ${s.loading ? `<div class="small">Loading docks…</div>` : ""}
      ${s.status ? `<div class="small" style="color:#93c5fd">${esc(s.status)}</div>` : s.tool ? `<div class="small" style="color:#93c5fd">${esc(TOOL_HINT[s.tool] || "")}</div>` : ""}
      ${s.error ? `<div class="small" style="color:#f87171">${esc(s.error)}</div>` : ""}
      ${b ? (() => {
        const FITTINGS = ["power-pedestal", "dock-box", "cleat", "gate", "mooring-whip", "fire-station", "life-ring-station", "fence-panel", "security-light", "trash-receptacle"];
        const fittings = FITTINGS.reduce((n, p) => n + count(p), 0), junctions = count("walkway-junction"), ramps = count("gangway");
        return `<div class="small" style="color:var(--muted)">${count("walkway-module") + count("corner-module")} float modules · ${count("finger")} fingers · ${count("pile")} piles (${count("pile-guide")} guided)${fittings ? ` · ${fittings} fittings` : ""}${junctions ? ` · ${junctions} junction${junctions === 1 ? "" : "s"}` : ""}${ramps ? ` · ${ramps} gangway${ramps === 1 ? "" : "s"}` : ""} · ${b.connections.length} joints</div>`;
      })() : ""}
      ${b && b.warnings.length ? `<div class="small" style="color:#fbbf24">${b.warnings.slice(0, 4).map((w) => esc(w.message)).join("<br>")}${b.warnings.length > 4 ? `<br>and ${b.warnings.length - 4} more` : ""}</div>` : ""}
      ${failing.length ? `<div class="small" style="color:#fbbf24">${failing.slice(0, 4).map((c) => `${esc(c.check)}: ${esc(c.value)} (${esc(c.rule)}, ${esc(c.source)})`).join("<br>")}</div>` : ""}
      ${b && b.bom.length ? `<details><summary class="small" style="cursor:pointer">Bill of materials (${b.bom.length} lines)</summary><table class="small" style="width:100%;border-collapse:collapse;margin-top:4px;">${b.bom.map((r) => `<tr><td>${esc(r.item)}<div style="color:var(--muted)">${esc(r.spec)}</div></td><td style="text-align:right;white-space:nowrap;vertical-align:top">${esc(r.qty)} ${esc(r.unit)}</td></tr>`).join("")}</table></details>` : ""}
    </div>`;
  }
  function mountPanel(el) {
    if (!el) return;
    el.innerHTML = panelHtml();
    el.onclick = async (e) => {
      const a = e.target.closest("[data-dock]")?.getAttribute("data-dock"); if (!a || e.target.matches("input,select")) return;
      if (["walkway", "fingers", "pile", "select", "fitting", "ramp"].includes(a)) { if (s.tool === a) stopTool(); else startTool(a); requestRender(); }
      else if (a === "done") { stopTool(); requestRender(); }
      else if (a === "undo") await undo(); else if (a === "redo") await redo();
      else if (a === "from-slips") await fingersFromSlipsForSelected();
      else if (a === "suggest") suggest(); else if (a === "suggest-fit") suggestFit(); else if (a === "add-all") await addAllSuggestions();
      else if (a === "delete") await deleteSelected();
    };
    el.onchange = async (e) => {
      const t = e.target, a = t.getAttribute("data-dock");
      if (a === "show") { s.show = t.checked; drawAll(); return; }
      if (a === "assembly") { await openAssembly(t.value); return; }
      const o = t.getAttribute("data-dock-opt"); if (o) { if (o === "fitType") { s.opts.fitType = t.value; return; } const v = Number(t.value); s.opts[o] = Number.isFinite(v) && v > 0 ? v : null; return; }
      const p = t.getAttribute("data-dock-param");
      if (p) { const v = t.type === "number" ? (t.value === "" ? null : Number(t.value)) : t.value; await updateSelected({ [p]: v }); }
    };
  }
  // Clicking a dock part with no docks tool on selects it and switches to Select, as clicking a
  // slip selects the slip. Clicks that miss the docks (slips, water) are left to the slip tool,
  // and nothing is taken while a slip draw tool is running.
  {
    const el = map.getContainer();
    let down = null, takeClick = 0;
    el.addEventListener("pointerdown", (e) => { down = !s.tool && s.show && s.active && s.built && (e.button == null || e.button === 0) && !isOtherToolActive?.() ? { x: e.clientX, y: e.clientY } : null; }, true);
    el.addEventListener("pointerup", (e) => {
      if (!down || s.tool) { down = null; return; }
      const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y) > 6; down = null;
      if (moved) return;
      const c = hitComponent(map.mouseEventToLatLng(e));
      if (!c) return;
      takeClick = Date.now();
      startTool("select"); s.sel = c.feature; s.status = ""; drawAll(); requestRender();
    }, true);
    el.addEventListener("click", (e) => { if (Date.now() - takeClick < 400) { e.stopPropagation(); e.preventDefault(); takeClick = 0; } }, true);
  }

  // Ctrl+Z / Ctrl+Shift+Z while docks are open and nothing is being typed
  document.addEventListener("keydown", (e) => {
    if (!s.active || !s.assembly || !(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "z" || /^(input|textarea|select)$/i.test(e.target?.tagName || "")) return;
    if (!s.tool && !s.sel) return; // the slip tool owns Ctrl+Z otherwise
    e.preventDefault(); e.stopPropagation(); (e.shiftKey ? redo : undo)();
  }, true);

  return {
    mountPanel,
    stopTool: () => { if (s.tool) { stopTool(); requestRender(); } },
    isActive: () => !!s.tool,
    // the Dock Builder opened / closed: its docks are on the map (and clickable) only while it's open
    setActive: (on) => { s.active = !!on; if (!on && s.tool) stopTool(); drawAll(); },
    sync: () => { const m = getMarinaId() || ""; if (m !== s.marinaId) loadMarina(m); },
  };
}
